import React, { useEffect } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import FlyoutGarage from "src/components/FlyoutGarage";
import {
    ButtonLabel,
    FlyoutButton,
    GarageContainer,
    GarageHeaderButton,
    StyledCloseIcon,
    StyledGarageIcon,
    StyledImage,
} from "./FlyoutStyledComponents";
import "./FlyoutStyles.scss";
import { FlyoutEnabledProps, FlyoutProps, WithSettings } from "./FlyoutTypes";
import { useFrame } from "react-frame-component";
import { useMobileMediaQuery } from "Shared/Responsive";

const ButtonDecoration = ({ hideTheLabel, settings }: WithSettings & { hideTheLabel?: boolean }) => {
    if (settings?.icon_url) {
        return <StyledImage src={settings?.icon_url} hideTheLabel={hideTheLabel} />;
    }
    return <StyledGarageIcon settings={settings} hideTheLabel={hideTheLabel} />;
};

const flyoutEnabled = ({ isMobile, settings }: FlyoutEnabledProps) => {
    if (isMobile) {
        return "disabled" !== settings?.mobile_flyout_placement;
    }
    return "disabled" !== settings?.flyout_placement;
};

const Flyout = ({
    garageType,
    isAccountLoading,
    isAppOpen,
    isOpen,
    settings,
    showButtonLabel = false,
    t,
    toggleBodyHide,
}: FlyoutProps & { showButtonLabel?: boolean } & WithTranslation) => {
    const isMobile = useMobileMediaQuery();
    const iframe = useFrame();

    /**
     * This adds the flyout-garage-enable class defined in src/AppGlobalStyles.tsx
     * It helps override some of the default iframe styles.
     *
     * Effects in React cannot be conditional, which is why we have an additional
     * check of app open within the effect.
     *
     * The returned function is a callback that takes care of the cleanup.
     */
    useEffect(() => {
        if (!flyoutEnabled({ isMobile, settings }) || isAppOpen) return;
        iframe.document.body.classList.add("flyout-garage-enable");
        return () => iframe.document.body.classList.remove("flyout-garage-enable");
    }, [iframe.document.body.classList, isAppOpen, isMobile, settings]);

    if (isAccountLoading || isAppOpen) return null;
    if (!flyoutEnabled({ isMobile, settings })) return null;

    const flyoutPosition = isMobile ? settings?.mobile_flyout_placement : settings?.flyout_placement;
    const ButtonPositionClass = `FlyoutButton--${flyoutPosition}`;
    const GaragePositionClass = `FlyoutGarage--${flyoutPosition}`;

    const isOpenClass = isOpen ? "GarageIsOpen" : "";
    const isMobileClass = isMobile ? "GarageIsMobile" : "";
    const GarageTypeClass = `GarageType--${garageType}`;
    const ViewportIsMobileClass = isMobile ? "ViewportIsMobile" : "";

    // Hide the label when the garage is closed and showButtonLabel is false.
    const hideTheLabel = !isOpen && !showButtonLabel;
    const SmallGarageButtonClass = hideTheLabel ? "SmallGarageButton" : "";

    return (
        <div className="FlyoutContainer">
            <div
                onClick={toggleBodyHide}
                className={`FlyoutButton ${ButtonPositionClass} ${isOpenClass} ${isMobileClass} ${GarageTypeClass} ${SmallGarageButtonClass}`}
            >
                {!(isMobile && isOpen) && (
                    <FlyoutButton>
                        <ButtonDecoration hideTheLabel={hideTheLabel} settings={settings} />
                        {!hideTheLabel && <ButtonLabel>{t("Flyout.ButtonFlyout.label")}</ButtonLabel>}
                    </FlyoutButton>
                )}
            </div>
            {isOpen && (
                <GarageContainer
                    className={`FlyoutGarage ${GaragePositionClass} ${GarageTypeClass} ${ViewportIsMobileClass}`}
                >
                    {isMobile && (
                        <GarageHeaderButton onClick={toggleBodyHide}>
                            <ButtonDecoration settings={settings} />
                            <ButtonLabel>{t("Flyout.ButtonFlyout.label")}</ButtonLabel>
                            <StyledCloseIcon />
                        </GarageHeaderButton>
                    )}
                    <FlyoutGarage />
                </GarageContainer>
            )}
        </div>
    );
};

export default withTranslation()(Flyout);
